import React, { useEffect, useState, useContext } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  Avatar,
  Chip,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  Select,
  MenuItem,
  TextField,
  BottomNavigation,
  BottomNavigationAction,
  Container,
  CircularProgress,
  AvatarGroup
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { keyframes } from '@mui/system';
import {
  Logout as LogoutIcon,
  EmojiEvents as TrophyIcon,
  SportsEsports as GameIcon,
  Person as PersonIcon,
  Add as AddIcon,
  Casino as CasinoIcon,
  Star as StarIcon,
  Psychology as OlimpicoIcon,
  EmojiEvents as HeroeIcon,
  Pets as DragonIcon,
  Shield as CaballeroIcon,
  Whatshot as DesafianteIcon,
  Security as DefensorIcon,
  AccountBalance as FaraonIcon,
  School as SacerdoteIcon,
  AutoAwesome as FaerieIcon,
  DarkMode as SombraIcon,
  AccountTree as TitanIcon,
  AllInclusive as EternoIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as ProfileIcon,
  Casino as DecksIcon,
  SportsEsports as MatchesIcon,
  Schedule as ScheduleIcon,
  TrendingUp as RemontadaIcon,
  LocalFireDepartment as PalizaIcon,
  Extension as ComboIcon,
  Shield as ResilienciaIcon,
  Person as SobrevivienteIcon,
  Bolt as RapidaIcon,
  SportsEsports
} from '@mui/icons-material';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';
import MatchCard from '../components/MatchCard';
import StatsCards from '../components/StatsCards';
import RaceChip, { RAZA_ICONS } from '../components/RaceChip';
import RecentMatchesSection from '../components/RecentMatchesSection';
import SectionTitle from '../components/SectionTitle';
import PageTitle from '../components/PageTitle';
import OptimizedImage from '../components/OptimizedImage';
import HuevoDragon from '../assets/medals2/Huevo_de_Dragon_v2.png';

// Definir keyframes para la animación de gradiente
const gradientGlowAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

function ProfilePage() {
  const [matches, setMatches] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [decks, setDecks] = useState([]);
  const [activeSection, setActiveSection] = useState('profile');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deckToDelete, setDeckToDelete] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deckToEdit, setDeckToEdit] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    raza: ''
  });
  const [editError, setEditError] = useState(null);
  const [isLoadingDecks, setIsLoadingDecks] = useState(true);
  const [error, setError] = useState(null);
  
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await api.post('/api/auth/logout');
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const fetchData = async () => {
    try {
      console.log('Iniciando fetchData...');
      const [matchesRes, decksRes] = await Promise.all([
        api.get('/api/matches/me'),
        api.get('/api/decks')
      ]);
      
      console.log('Matches response:', matchesRes.data);
      console.log('Decks response:', decksRes.data);
      
      setMatches(matchesRes.data);
      setDecks(decksRes.data);
      setError(null);
    } catch (error) {
      console.error('Error en fetchData:', error);
      setError('Error al cargar los datos');
    } finally {
      setIsLoadingDecks(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/api/users');
      const map = {};
      res.data.forEach((u) => {
        map[u.id] = u.username;
      });
      setUsersMap(map);
    } catch (error) {
      console.error(error);
    }
  };

  const sortDecks = (decks) => {
    return [...decks].sort((a, b) => {
      if (a.is_default && !b.is_default) return -1;
      if (!a.is_default && b.is_default) return 1;
      return b.id - a.id;
    });
  };

  const handleSetDefaultDeck = async (deckId) => {
    const previousDefault = decks.find(d => d.is_default);
    setIsAnimating(true);
    
    try {
      await api.put(`/api/decks/${deckId}/default`);
      setDecks(prevDecks => {
        const updatedDecks = prevDecks.map(deck => ({
          ...deck,
          is_default: deck.id === deckId
        }));
        return sortDecks(updatedDecks);
      });
    } catch (error) {
      console.error('Error setting default deck:', error);
    } finally {
      setTimeout(() => {
        setIsAnimating(false);
      }, 300);
    }
  };

  const getUsernamesFromPlayers = (players) => {
    if (!Array.isArray(players)) return [];
    return players.map(player => ({
      id: player.user_id,
      name: usersMap[player.user_id] || player.user_id
    }));
  };

  const getGanador = (match) => {
    const winningTeam = match.winning_team === 'team1' ? match.team1 : match.team2;
    return getUsernamesFromPlayers(winningTeam?.players || []);
  };

  const handleOpenDeleteDialog = (e, deck) => {
    e.stopPropagation();
    setDeckToDelete(deck);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeckToDelete(null);
  };

  const handleOpenEditDialog = (e, deck) => {
    e.stopPropagation();
    setDeckToEdit(deck);
    setEditForm({
      name: deck.name,
      raza: deck.raza
    });
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setDeckToEdit(null);
    setEditForm({ name: '', raza: '' });
    setEditError(null);
  };

  const handleDeleteDeck = async (deckId) => {
    try {
      await api.delete(`/api/decks/${deckId}`);
      fetchData();
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Error deleting deck:', error);
    }
  };

  const handleEditDeck = async () => {
    if (!editForm.name.trim()) {
      setEditError('El nombre del mazo es obligatorio');
      return;
    }

    if (decks.some(deck => 
      deck.id !== deckToEdit.id && 
      deck.name.toLowerCase() === editForm.name.trim().toLowerCase()
    )) {
      setEditError('Ya existe un mazo con este nombre');
      return;
    }

    try {
      await api.put(`/api/decks/${deckToEdit.id}`, editForm);
      fetchData();
      handleCloseEditDialog();
    } catch (error) {
      console.error('Error updating deck:', error);
      setEditError(
        error.response?.data?.message || 
        'Error al actualizar el mazo. Por favor, inténtalo de nuevo.'
      );
    }
  };

  useEffect(() => {
    console.log('ProfilePage montado, user:', user);
    if (!user) {
      console.log('No hay usuario, redirigiendo a login');
      navigate('/login');
      return;
    }
    fetchData();
    fetchUsers();
  }, [user, navigate]);

  // Agregar efecto para reiniciar el scroll
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!user) {
    return null;
  }

  // Componente para la sección de perfil
  const ProfileSection = () => {
    console.log('Renderizando ProfileSection');
    return (
      <Paper
        elevation={0}
        sx={{
          p: 1.8,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper'
        }}
      >
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: 'primary.main',
                  fontSize: '1.5rem'
                }}
              >
                {user?.username?.[0]?.toUpperCase()}
              </Avatar>
              <Box
                component="img"
                src={HuevoDragon}
                alt="Dragon Egg"
                sx={{
                  position: 'absolute',
                  top: -10,
                  right: -10,
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  animation: `${gradientGlowAnimation} 3s ease infinite`,
                  border: '2px solid',
                  borderColor: 'primary.main',
                  bgcolor: 'background.paper',
                  p: 0.5,
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: -2,
                    left: -2,
                    right: -2,
                    bottom: -2,
                    borderRadius: '50%',
                    background: 'linear-gradient(45deg, #ff00ff, #00ffff, #ff00ff)',
                    backgroundSize: '200% 200%',
                    animation: `${gradientGlowAnimation} 3s ease infinite`,
                    zIndex: -1,
                    filter: 'blur(8px)',
                    opacity: 0.7
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" fontWeight="bold">
                    {user?.username}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ID #{user?.id}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Miembro desde {dayjs(user?.created_at).format('MMMM YYYY')}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={handleLogout}
                startIcon={<LogoutIcon />}
                sx={{
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontSize: '0.85rem'
                }}
              >
                Cerrar Sesión
              </Button>
            </Box>
          </Box>
        </Stack>
      </Paper>
    );
  };

  // Componente para la sección de mazos
  const DecksSection = () => {
    const sortedDecks = sortDecks(decks);
    const [isAnimating, setIsAnimating] = useState(false);
    const [previousDefaultId, setPreviousDefaultId] = useState(null);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [newDeck, setNewDeck] = useState({
      name: '',
      raza: 'Olimpico'
    });
    const [createError, setCreateError] = useState(null);
    
    const handleSetDefaultDeck = async (deckId) => {
      const previousDefault = decks.find(d => d.is_default);
      setPreviousDefaultId(previousDefault?.id);
      setIsAnimating(true);
      
      try {
        await api.put(`/api/decks/${deckId}/default`);
        setDecks(prevDecks => {
          const updatedDecks = prevDecks.map(deck => ({
            ...deck,
            is_default: deck.id === deckId
          }));
          return sortDecks(updatedDecks);
        });
      } catch (error) {
        console.error('Error setting default deck:', error);
      } finally {
        setTimeout(() => {
          setIsAnimating(false);
          setPreviousDefaultId(null);
        }, 300);
      }
    };

    const handleCreateDeck = async (e) => {
      e.preventDefault();
      
      if (!newDeck.name.trim()) {
        setCreateError('El nombre del mazo es obligatorio');
        return;
      }

      if (decks.some(deck => deck.name.toLowerCase() === newDeck.name.trim().toLowerCase())) {
        setCreateError('Ya existe un mazo con este nombre');
        return;
      }

      try {
        const res = await api.post('/api/decks', newDeck);
        setDecks([...decks, res.data]);
        setNewDeck({ name: '', raza: 'Olimpico' });
        setCreateError(null);
        setCreateDialogOpen(false);
      } catch (error) {
        console.error('Error creating deck:', error);
        setCreateError(
          error.response?.data?.message || 
          'Error al crear el mazo. Por favor, inténtalo de nuevo.'
        );
      }
    };

    return (
      <SectionTitle 
        title="Mis Mazos"
        headerContent={
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontSize: '0.85rem'
            }}
          >
            Crear Mazo
          </Button>
        }
      >
        {isLoadingDecks ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={40} />
          </Box>
        ) : sortedDecks.length > 0 ? (
          <Stack spacing={1.5}>
            {sortedDecks.map((deck) => (
              <Box
                key={deck.id}
                sx={{
                  transition: 'all 0.3s ease-in-out',
                  transform: deck.is_default ? 'scale(1.02)' : 'scale(1)',
                  zIndex: deck.is_default ? 2 : 1,
                }}
              >
                <DeckPreviewCard 
                  deck={deck} 
                  onSetDefault={handleSetDefaultDeck}
                  onEdit={handleOpenEditDialog}
                  onDelete={handleOpenDeleteDialog}
                  isAnimating={isAnimating}
                  wasDefault={deck.id === previousDefaultId}
                />
              </Box>
            ))}
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/decks')}
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontSize: '0.85rem',
                mt: 1,
                color: 'primary.main'
              }}
            >
              Ver todos los mazos
            </Button>
          </Stack>
        ) : (
          <Box sx={{ 
            py: 2,
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            gap: 1
          }}>
            <CasinoIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
            <Typography color="text.secondary">
              No hay mazos creados
            </Typography>
          </Box>
        )}

        {/* Modal de creación de mazo */}
        <Dialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: 2,
              width: '100%',
              maxWidth: '400px',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }
          }}
        >
          <DialogTitle sx={{ 
            pb: 1,
            fontWeight: 'bold',
            color: '#c89e38',
            fontSize: '1rem',
            lineHeight: 0.8
          }}>
            Nuevo Mazo
          </DialogTitle>
          <DialogContent sx={{ pb: 2 }}>
            {createError && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  borderRadius: 2
                }}
              >
                {createError}
              </Alert>
            )}
            <Stack spacing={2} component="form" onSubmit={handleCreateDeck} sx={{ mt: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="Nombre del Mazo"
                value={newDeck.name}
                onChange={(e) => setNewDeck({ ...newDeck, name: e.target.value })}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    borderColor: 'divider',
                    '&:hover': {
                      borderColor: 'primary.main'
                    }
                  }
                }}
              />
              <Typography variant="subtitle2" color="text.secondary">
                Raza
              </Typography>
              <FormControl fullWidth>
                <Select
                  size="small"
                  value={newDeck.raza}
                  onChange={(e) => setNewDeck({ ...newDeck, raza: e.target.value })}
                  sx={{
                    borderRadius: 1.5,
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'divider'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main'
                    }
                  }}
                >
                  {['Olimpico', 'Heroe', 'Dragon', 'Caballero', 'Desafiante', 'Defensor', 'Faraon', 'Sacerdote', 'Faerie', 'Sombra', 'Titan', 'Eterno'].map((raza) => {
                    const RazaIcon = RAZA_ICONS[raza] || CasinoIcon;
                    return (
                      <MenuItem key={raza} value={raza}>
                        <RazaIcon fontSize="small" sx={{ color: 'primary.main' }} />
                        {raza}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setCreateDialogOpen(false)}
              variant="outlined"
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontSize: '0.9rem',
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main'
                }
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreateDeck}
              variant="contained"
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontSize: '0.9rem',
                bgcolor: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.dark'
                }
              }}
            >
              Crear
            </Button>
          </DialogActions>
        </Dialog>
      </SectionTitle>
    );
  };

  console.log('Estado actual:', {
    activeSection,
    loading: isLoadingDecks,
    error,
    decksCount: decks.length,
    matchesCount: matches.length
  });

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 1 },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '90%',
      maxWidth: '500px',
      mx: 'auto',
      bgcolor: 'transparent'
    }}>
      <Stack spacing={1} sx={{ width: '100%', bgcolor: 'transparent' }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          width: '100%',
          position: 'relative'
        }}>
          <PageTitle title="Perfil" />
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontSize: '0.85rem',
              position: 'absolute',
              right: 0
            }}
          >
            Cerrar Sesión
          </Button>
        </Box>
        <Stack spacing={2}>
          {/* Perfil siempre visible */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              mb: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: 'primary.main',
                    fontSize: '1.5rem'
                  }}
                >
                  {user?.username?.[0]?.toUpperCase()}
                </Avatar>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" fontWeight="bold">
                      {user?.username}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ID #{user?.id}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Miembro desde {dayjs(user?.created_at).format('MMMM YYYY')}
                  </Typography>
                </Box>
              </Box>
              <Box
                component="img"
                src={HuevoDragon}
                alt="Dragon Egg"
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  animation: `${gradientGlowAnimation} 3s ease infinite`,
                  border: '2px solid',
                  borderColor: 'primary.main',
                  bgcolor: 'background.paper',
                  p: 0.5,
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: -2,
                    left: -2,
                    right: -2,
                    bottom: -2,
                    borderRadius: '50%',
                    background: 'linear-gradient(45deg, #ff00ff, #00ffff, #ff00ff)',
                    backgroundSize: '200% 200%',
                    animation: `${gradientGlowAnimation} 3s ease infinite`,
                    zIndex: -1,
                    filter: 'blur(8px)',
                    opacity: 0.7
                  }
                }}
              />
            </Box>

            <Box sx={{ mt: 2 }}>
              <StatsCards matches={matches} user={user} />
            </Box>
          </Paper>
          
          {/* Sección de Mazos */}
          <DecksSection />

          {/* Sección de Última Partida */}
          <SectionTitle 
            title="Última Partida"
            headerContent={
              <Button
                variant="contained"
                size="small"
                startIcon={<SportsEsports />}
                onClick={() => navigate('/my-matches')}
                sx={{
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontSize: '0.85rem',
                  minWidth: 'auto',
                  py: 0.2,
                  gap: 0,
                  '& .MuiButton-startIcon': {
                    marginRight: 0.5,
                    marginLeft: 0
                  }
                }}
              >
                Ver todas
              </Button>
            }
          >
            {matches.length > 0 ? (
              <MatchCard
                match={matches[0]}
                usersMap={usersMap}
                onClick={() => navigate(`/match/${matches[0].id}`)}
              />
            ) : (
              <Box sx={{ 
                py: 2,
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                gap: 1
              }}>
                <SportsEsports sx={{ fontSize: 40, color: 'text.disabled' }} />
                <Typography color="text.secondary">
                  No hay partidas recientes
                </Typography>
              </Box>
            )}
          </SectionTitle>
        </Stack>
      </Stack>

      {/* Diálogos */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        PaperProps={{
          sx: {
            borderRadius: 2,
            width: '100%',
            maxWidth: '400px',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            bgcolor: 'background.paper'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          fontWeight: 'bold',
          color: '#c89e38',
          fontSize: '1rem',
          lineHeight: 0.8
        }}>
          Confirmar eliminación
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography>
            ¿Estás seguro de que quieres eliminar el mazo "{deckToDelete?.name}"?
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseDeleteDialog}
            variant="outlined"
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontSize: '0.9rem',
              borderColor: 'divider',
              '&:hover': {
                borderColor: 'primary.main'
              }
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => handleDeleteDeck(deckToDelete?.id)}
            variant="contained"
            color="error"
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontSize: '0.9rem',
              bgcolor: 'error.main',
              '&:hover': {
                bgcolor: 'error.dark'
              }
            }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        PaperProps={{
          sx: {
            borderRadius: 2,
            width: '100%',
            maxWidth: '400px',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            bgcolor: 'background.paper'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          fontWeight: 'bold',
          color: '#c89e38',
          fontSize: '1rem',
          lineHeight: 0.8
        }}>
          Editar Mazo
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          {editError && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 2,
                borderRadius: 2
              }}
            >
              {editError}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="Nombre del Mazo"
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: 'primary.main'
                  }
                }
              }}
            />
            <Typography variant="subtitle2" color="text.secondary">
              Raza
            </Typography>
            <FormControl fullWidth>
              <Select
                size="small"
                value={editForm.raza}
                onChange={(e) => setEditForm({ ...editForm, raza: e.target.value })}
                sx={{
                  borderRadius: 1.5,
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'divider'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main'
                  }
                }}
              >
                {['Olimpico', 'Heroe', 'Dragon', 'Caballero', 'Desafiante', 'Defensor', 'Faraon', 'Sacerdote', 'Faerie', 'Sombra', 'Titan', 'Eterno'].map((raza) => {
                  const RazaIcon = RAZA_ICONS[raza] || CasinoIcon;
                  return (
                    <MenuItem key={raza} value={raza}>
                      <RazaIcon fontSize="small" sx={{ color: 'primary.main' }} />
                      {raza}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseEditDialog}
            variant="outlined"
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontSize: '0.9rem',
              borderColor: 'divider',
              '&:hover': {
                borderColor: 'primary.main'
              }
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleEditDeck}
            variant="contained"
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontSize: '0.9rem',
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.dark'
              }
            }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const DeckPreviewCard = ({ 
  deck, 
  onSetDefault, 
  onEdit, 
  onDelete,
  isAnimating,
  wasDefault
}) => {
  const isDefault = deck.is_default;
  const RazaIcon = RAZA_ICONS[deck.raza] || CasinoIcon;
  
  return (
    <Paper
      elevation={isDefault ? 2 : 0}
      sx={{
        p: 1.2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: isDefault ? 'primary.main' : 'divider',
        bgcolor: 'background.paper',
        transition: 'all 0.3s ease-in-out',
        transform: isDefault ? 'translateY(-2px)' : 'none',
        '&:hover': {
          borderColor: 'primary.main',
          transform: isDefault ? 'translateY(-2px)' : 'translateY(-1px)',
          boxShadow: (theme) => isDefault ? theme.shadows[4] : theme.shadows[1]
        }
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        minHeight: '40px'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (!isDefault) {
                onSetDefault(deck.id);
              }
            }}
            sx={{
              p: 0.5,
              color: isDefault ? 'primary.main' : 'text.disabled',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                color: 'primary.main',
                transform: 'scale(1.1)'
              }
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2 }}>
              <StarIcon 
                sx={{ 
                  fontSize: '1.3rem',
                  transition: 'all 0.3s ease-in-out',
                  animation: isAnimating ? 
                    (isDefault ? 'starColorIn 0.3s ease-in-out' : 
                     wasDefault ? 'starColorOut 0.3s ease-in-out' : 'none') : 'none',
                  '@keyframes starColorIn': {
                    '0%': {
                      color: 'text.disabled'
                    },
                    '100%': {
                      color: 'primary.main'
                    }
                  },
                  '@keyframes starColorOut': {
                    '0%': {
                      color: 'primary.main'
                    },
                    '100%': {
                      color: 'text.disabled'
                    }
                  },
                  ...(isDefault ? {
                    transform: 'rotate(360deg)'
                  } : {
                    strokeWidth: 1,
                    stroke: 'currentColor',
                    fill: 'transparent'
                  })
                }} 
              />
              {isDefault && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'primary.main',
                    fontStyle: 'italic',
                    fontSize: '0.65rem',
                    lineHeight: 1,
                    animation: isAnimating ? 'fadeIn 0.3s ease-in-out' : 'none',
                    '@keyframes fadeIn': {
                      '0%': {
                        opacity: 0,
                        transform: 'translateY(-4px)'
                      },
                      '100%': {
                        opacity: 1,
                        transform: 'translateY(0)'
                      }
                    }
                  }}
                >
                  Default
                </Typography>
              )}
            </Box>
          </IconButton>
          <Box>
            <Typography 
              variant="subtitle2" 
              fontWeight="medium"
              sx={{ fontSize: '0.9rem', lineHeight: 1.3 }}
            >
              {deck.name}
            </Typography>
            <Stack direction="row" spacing={0.8} alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                ID: {deck.id}
              </Typography>
              <RaceChip race={deck.raza} />
            </Stack>
          </Box>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          gap: 0.5
        }}>
          <IconButton 
            size="small" 
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(e, deck);
            }}
            sx={{ p: 0.5 }}
          >
            <EditIcon sx={{ fontSize: '1.1rem' }} />
          </IconButton>
          <IconButton 
            size="small" 
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(e, deck);
            }}
            disabled={isDefault}
            sx={{
              p: 0.5,
              '&.Mui-disabled': {
                color: 'action.disabled'
              }
            }}
          >
            <DeleteIcon sx={{ fontSize: '1.1rem' }} />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
};

// Agregar una función para obtener el color del tier
const getTierColor = (tier) => {
  switch (tier) {
    case 'Amistoso':
      return { bg: '#4caf50', text: '#fff' };
    case 'Amistoso c/ Arbitro':
      return { bg: '#2196f3', text: '#fff' };
    case 'Arena':
      return { bg: '#ff9800', text: '#fff' };
    case 'Torneo':
      return { bg: '#f44336', text: '#fff' };
    default:
      return { bg: 'action.hover', text: 'text.primary' };
  }
};

// Agregar una función para obtener el icono del highlight
const getHighlightIcon = (highlight) => {
  switch (highlight) {
    case 'Remontada':
      return <RemontadaIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Paliza':
      return <PalizaIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Combo':
      return <ComboIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Resiliencia':
      return <ResilienciaIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Sobreviviente':
      return <SobrevivienteIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Rapida':
      return <RapidaIcon sx={{ fontSize: '1.2rem' }} />;
    default:
      return null;
  }
};

export default ProfilePage;